import BenchlingConfig from 'BenchlingConfig';
import {AgentType, ChatType} from 'graphql-client/__generated/graphqlGlobalTypes';
import type {TargetItemInfo} from 'llm/chat-library/getTargetItem';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {listToReadable} from 'util/NameUtil';
import {assertNever} from 'util/Types';

// Single source of truth for UI order: toolbar agent types (left-to-right).
export const ORDERED_AGENT_TYPES: Array<AgentType> = [
  AgentType.ASSISTANT,
  AgentType.ASK,
  AgentType.DEEP_RESEARCH,
  AgentType.DATA_CAPTURE,
  AgentType.ANALYSIS,
  AgentType.CO_SCIENTIST,
  AgentType.LEGACY_DATA_IMPORT,
  AgentType.ONBOARDING,
];

export function isAISidepanelEnabled(): boolean {
  return getChatTypesForNewAgentChat().length > 0;
}

/**
 * Ordered list of ChatTypes that can be selected when starting a new chat.
 *
 * Returns chat options available for starting new chats.
 */
export function getChatTypesForNewAgentChat(): Array<ChatType> {
  // Analysis Agent is not folded into Assistant (BNCH-151369); keep a distinct ANALYSIS chat type so
  // new-chat flows can still target analysis-only threads when agent switching is on.
  const allChatTypes = BenchlingConfig.LLM_ENABLE_AGENT_SWITCH
    ? [ChatType.UNIFIED, ChatType.ANALYSIS]
    : [ChatType.DEEP_RESEARCH, ChatType.DATA_CAPTURE, ChatType.ANALYSIS];
  return allChatTypes.filter((chatType) => getAvailableAgentTypes([chatType]).length > 0);
}

/**
 * Parse the value out of a URL like/chats/new?agent=<agentTypeQSValue> (see: AIRoutes.ts).
 *
 * NOTE: These URLs may be put in marketing emails, so we should avoid changing the string values
 * when possible, i.e. maintain backwards compatibility. It is acceptable to have multiple querystring
 * values map to the same AgentType.
 */
export function getAgentTypeFromQueryStringValue(agentTypeQSValue: string | null): AgentType | null {
  switch (agentTypeQSValue) {
    case 'ask':
      return AgentType.ASK;
    case 'deep-research':
      return AgentType.DEEP_RESEARCH;
    case 'compose':
      return AgentType.DATA_CAPTURE;
    case 'analysis':
      return AgentType.ANALYSIS;
    case 'co-scientist':
      return AgentType.CO_SCIENTIST;
    case 'assistant':
      return AgentType.ASSISTANT;
    default:
      // Non-exhaustive: we will add more agent types here if requested.
      return null;
  }
}

/**
 * Note: this is only used for internal feedback submission.
 */
export function getChatTypeText(chatType: ChatType): string {
  switch (chatType) {
    case ChatType.UNIFIED:
      return 'Chat';
    case ChatType.DEEP_RESEARCH:
      return 'deep research';
    case ChatType.DATA_CAPTURE:
      return 'Compose';
    case ChatType.LEGACY_DATA_IMPORT:
      return 'Legacy Data Import';
    case ChatType.ANALYSIS:
      return 'Analysis';
    case ChatType.ONBOARDING:
      return 'Onboarding';
    default:
      throw new Error(`Unknown chat type: ${assertNever(chatType)}`);
  }
}

export function doesAgentTypePreferExpandedSidepanel(agentType: AgentType): boolean {
  switch (agentType) {
    case AgentType.DATA_CAPTURE:
      return true;
    case AgentType.LEGACY_DATA_IMPORT:
      return true;
    case AgentType.ASK:
      return false;
    case AgentType.DEEP_RESEARCH:
      return false;
    case AgentType.ANALYSIS:
      return false;
    case AgentType.CO_SCIENTIST:
      return false;
    case AgentType.ASSISTANT:
      return false;
    case AgentType.ONBOARDING:
      return false;
    default:
      assertNever(agentType);
      return false;
  }
}

/**
 * Return the chat type to use when creating a new chat for the given agent.
 *
 * When agent switching is enabled, DEEP_RESEARCH and DATA_CAPTURE are remapped
 * to UNIFIED so that new chats support agent switching. Other chat types
 * (e.g. ANALYSIS and LEGACY_DATA_IMPORT) keep their own island.
 */
export function getChatTypeForNewAgentChat(agentType: AgentType): ChatType {
  switch (agentType) {
    case AgentType.DEEP_RESEARCH:
      return BenchlingConfig.LLM_ENABLE_AGENT_SWITCH ? ChatType.UNIFIED : ChatType.DEEP_RESEARCH;
    case AgentType.ASK:
      return BenchlingConfig.LLM_ENABLE_AGENT_SWITCH ? ChatType.UNIFIED : ChatType.DEEP_RESEARCH;
    case AgentType.DATA_CAPTURE:
      return BenchlingConfig.LLM_ENABLE_AGENT_SWITCH ? ChatType.UNIFIED : ChatType.DATA_CAPTURE;
    case AgentType.ANALYSIS:
      return ChatType.ANALYSIS;
    case AgentType.CO_SCIENTIST:
      return ChatType.UNIFIED;
    case AgentType.ASSISTANT:
      return ChatType.UNIFIED;
    case AgentType.LEGACY_DATA_IMPORT:
      return ChatType.LEGACY_DATA_IMPORT;
    case AgentType.ONBOARDING:
      return ChatType.ONBOARDING;
    default:
      throw new Error(`Unknown agent type: ${assertNever(agentType)}`);
  }
}

export function getAgentTypeIcon(agentType: AgentType): BenchlingIconName | null {
  switch (agentType) {
    case AgentType.DEEP_RESEARCH:
      return 'file-magnifying-glass';
    case AgentType.DATA_CAPTURE:
      return 'pen';
    case AgentType.LEGACY_DATA_IMPORT:
      return 'import';
    case AgentType.ASK:
      return 'comments';
    case AgentType.ANALYSIS:
      return 'lightbulb';
    case AgentType.CO_SCIENTIST:
      return 'flask';
    case AgentType.ASSISTANT:
      return 'sparkle';
    case AgentType.ONBOARDING:
      return 'user';
    default:
      assertNever(agentType);
      return null;
  }
}

export function getAgentTypeText(agentType: AgentType): string {
  switch (agentType) {
    case AgentType.DEEP_RESEARCH:
      return 'Deep Research';
    case AgentType.ASK:
      return 'Ask';
    case AgentType.DATA_CAPTURE:
      return 'Compose';
    case AgentType.LEGACY_DATA_IMPORT:
      return 'Data Import';
    case AgentType.ANALYSIS:
      return 'Analysis';
    case AgentType.CO_SCIENTIST:
      return 'Co-scientist';
    case AgentType.ASSISTANT:
      return 'Assistant';
    case AgentType.ONBOARDING:
      return 'Onboarding';
    default:
      assertNever(agentType);
      throw new Error(`Unknown agent type: ${assertNever(agentType)}`);
  }
}

export function getAgentDescriptionForChatInput(agentType: AgentType): string | null {
  switch (agentType) {
    case AgentType.ASSISTANT:
      return 'Your all-in-one AI assistant';
    case AgentType.ASK:
      return 'Find answers fast';
    case AgentType.DEEP_RESEARCH:
      return 'Answer complex questions';
    case AgentType.DATA_CAPTURE:
      return 'Create lab entries';
    case AgentType.LEGACY_DATA_IMPORT:
      return 'Import and transcribe data';
    case AgentType.ANALYSIS:
      return null;
    case AgentType.CO_SCIENTIST:
      return null;
    case AgentType.ONBOARDING:
      // Onboarding Agent has its own interface.
      return null;
    default:
      assertNever(agentType);
      return null;
  }
}

/**
 * Agent types whose standalone UX is superseded by the Assistant agent (they remain available via the
 * unified assistant picker in the chat input).
 *
 * TODO(BNCH-151369): To fold Analysis Agent into Assistant, treat AgentType.ANALYSIS like the other
 * deprecated agents (include it here and in isAssistantUnifiedToolbarAgentType) once Analysis
 * workflows are owned by Assistant end-to-end.
 */
export function doesAssistantDeprecateAgent(agentType: AgentType): boolean {
  switch (agentType) {
    case AgentType.ASK:
    case AgentType.DEEP_RESEARCH:
    case AgentType.DATA_CAPTURE:
      return true;
    case AgentType.ANALYSIS:
      return false;
    case AgentType.ASSISTANT:
    case AgentType.LEGACY_DATA_IMPORT:
    case AgentType.CO_SCIENTIST:
    case AgentType.ONBOARDING:
      return false;
    default:
      assertNever(agentType);
      return false;
  }
}

/** Display names for agent modes superseded by Assistant, in toolbar order (for settings copy, tooltips, etc.). */
export function getAgentTypeLabelsReplacedByAssistant(): Array<string> {
  return ORDERED_AGENT_TYPES.filter((t) => doesAssistantDeprecateAgent(t)).map((t) => getAgentTypeText(t));
}

export function formatAgentTypeLabelsReplacedByAssistantEnglish(): string {
  return listToReadable(getAgentTypeLabelsReplacedByAssistant());
}

/** Assistant plus deprecated agents (Ask, Deep Research, Compose) shown as one toolbar control when agent switching is on. */
export function isAssistantUnifiedToolbarAgentType(agentType: AgentType): boolean {
  return agentType === AgentType.ASSISTANT || doesAssistantDeprecateAgent(agentType);
}

export function isAgentTypeEnabled(agentType: AgentType): boolean {
  switch (agentType) {
    case AgentType.DEEP_RESEARCH:
      return BenchlingConfig.LLM_ENABLE_AI_DEEP_RESEARCH;
    case AgentType.DATA_CAPTURE:
      return BenchlingConfig.LLM_ENABLE_AI_DATA_CAPTURE_ASSISTANT;
    case AgentType.LEGACY_DATA_IMPORT:
      return BenchlingConfig.LLM_ENABLE_AI_DATA_CAPTURE_ASSISTANT;
    case AgentType.ASK:
      return BenchlingConfig.LLM_ENABLE_AI_DEEP_RESEARCH;
    case AgentType.ANALYSIS:
      return BenchlingConfig.LLM_ENABLE_AI_ANALYSIS_AGENT;
    case AgentType.CO_SCIENTIST:
      return BenchlingConfig.LLM_ENABLE_CO_SCIENTIST;
    case AgentType.ASSISTANT:
      return BenchlingConfig.LLM_ENABLE_ASSISTANT_AGENT;
    case AgentType.ONBOARDING:
      return BenchlingConfig.LLM_ENABLE_AI_ONBOARDING;
    default:
      assertNever(agentType);
      return false;
  }
}

export function getAvailableAgentTypes(chatTypes: Array<ChatType>): Array<AgentType> {
  if (BenchlingConfig.LLM_ENABLE_AGENT_SWITCH) {
    // Re-map legacy chat types to UNIFIED in case we are dealing with an old chat.
    chatTypes = [
      ...new Set(
        chatTypes.map((chatType) =>
          chatType === ChatType.DEEP_RESEARCH || chatType === ChatType.DATA_CAPTURE
            ? ChatType.UNIFIED
            : chatType
        )
      ),
    ];
  }
  return ORDERED_AGENT_TYPES.filter(
    (agentType) => isAgentTypeEnabled(agentType) && chatTypes.includes(getChatTypeForNewAgentChat(agentType))
  );
}

/**
 * Get the default agent type for new chats.
 *
 * If initialAgentType is provided and available, it will be used.
 * Otherwise, defaults to the first available agent type.
 */
export function getDefaultAgentType(
  initialAgentType?: AgentType | null,
  chatTypes: Array<ChatType> = getChatTypesForNewAgentChat()
): AgentType | null {
  const availableAgentTypes = getAvailableAgentTypes(chatTypes);
  if (initialAgentType && availableAgentTypes.includes(initialAgentType)) {
    return initialAgentType;
  }
  return availableAgentTypes[0] ?? null;
}

export function getAgentTypePlaceholderText(
  agentType: AgentType,
  targetItem: TargetItemInfo | null = null
): string {
  switch (agentType) {
    case AgentType.DEEP_RESEARCH:
      return 'Write detailed reports based on your data';
    case AgentType.ASK:
      return 'Ask questions about your data';
    case AgentType.DATA_CAPTURE:
      if (targetItem != null) {
        return 'Capture data, keep notes, or ask questions';
      } else {
        return 'Draft an entry or template';
      }
    case AgentType.LEGACY_DATA_IMPORT:
      return 'Transcribe a file into a Benchling document';
    case AgentType.ANALYSIS:
      return 'Search and explore your analyses and datasets';
    case AgentType.CO_SCIENTIST:
      return 'Manage deep and long-running scientific work';
    case AgentType.ASSISTANT:
      return 'Ask questions, research, write, and analyze';
    case AgentType.ONBOARDING:
      return 'Answer questions about yourself';
    default:
      assertNever(agentType);
      return '';
  }
}

/**
 * doesAgentTypeSupportSources must also return true to allow public data to be used.
 */
export function doesAgentTypeSupportPublicData(agentType: AgentType): boolean {
  switch (agentType) {
    case AgentType.DEEP_RESEARCH:
      return BenchlingConfig.LLM_ENABLE_AI_DEEP_RESEARCH_PUBLIC_DATA;
    case AgentType.ASK:
      return false;
    case AgentType.DATA_CAPTURE:
      return false;
    case AgentType.LEGACY_DATA_IMPORT:
      return false;
    case AgentType.ANALYSIS:
      return false;
    case AgentType.CO_SCIENTIST:
      return false;
    case AgentType.ASSISTANT:
      return BenchlingConfig.LLM_ENABLE_AI_DEEP_RESEARCH_PUBLIC_DATA;
    case AgentType.ONBOARDING:
      return false;
    default:
      assertNever(agentType);
      return false;
  }
}

/**
 * Indicate if the agent backend supports setting CreateDisplayMessageChatEvent['sources'].
 */
export function doesAgentTypeSupportSources(agentType: AgentType): boolean {
  switch (agentType) {
    case AgentType.DEEP_RESEARCH:
      return true;
    case AgentType.DATA_CAPTURE:
      return false;
    case AgentType.ASK:
      return false;
    case AgentType.LEGACY_DATA_IMPORT:
      return false;
    case AgentType.ANALYSIS:
      return false;
    case AgentType.CO_SCIENTIST:
      return false;
    case AgentType.ASSISTANT:
      return true;
    case AgentType.ONBOARDING:
      return false;
    default:
      assertNever(agentType);
      return false;
  }
}

/**
 * Indicate if the agent type supports workspace context (active workspace item as context).
 */
export function doesAgentTypeSupportWorkspaceContext(agentType: AgentType): boolean {
  switch (agentType) {
    case AgentType.ASK:
      return true;
    case AgentType.DEEP_RESEARCH:
      return false;
    case AgentType.DATA_CAPTURE:
      return false;
    case AgentType.LEGACY_DATA_IMPORT:
      return false;
    case AgentType.ANALYSIS:
      return false;
    case AgentType.CO_SCIENTIST:
      return false;
    case AgentType.ASSISTANT:
      return true;
    case AgentType.ONBOARDING:
      return false;
    default:
      assertNever(agentType);
      return false;
  }
}

export function doesAgentTypeSupportTargetItem(agentType: AgentType): boolean {
  switch (agentType) {
    case AgentType.DATA_CAPTURE:
    case AgentType.ANALYSIS:
    case AgentType.ASSISTANT:
      return true;
    case AgentType.ASK:
    case AgentType.DEEP_RESEARCH:
    case AgentType.LEGACY_DATA_IMPORT:
    case AgentType.CO_SCIENTIST:
    case AgentType.ONBOARDING:
      return false;
    default:
      assertNever(agentType);
      return false;
  }
}

export function doesAgentTypeShowThinking(agentType: AgentType): boolean {
  switch (agentType) {
    case AgentType.DATA_CAPTURE:
    case AgentType.DEEP_RESEARCH:
    case AgentType.ASSISTANT:
      return true;
    case AgentType.ASK:
    case AgentType.LEGACY_DATA_IMPORT:
    case AgentType.ANALYSIS:
    case AgentType.CO_SCIENTIST:
    case AgentType.ONBOARDING:
      return false;
    default:
      assertNever(agentType);
      return false;
  }
}

/**
 * Indicate if the agent type supports realtime voice input.
 *
 * Assistant is intentionally a superset of Data Capture here, including
 * config-flagged Compose behavior, so both agent types share the existing rollout.
 */
export function doesAgentTypeSupportRealtimeVoice(agentType: AgentType): boolean {
  switch (agentType) {
    case AgentType.DATA_CAPTURE:
    case AgentType.ASSISTANT:
      return BenchlingConfig.LLM_ENABLE_REALTIME_VOICE_IN_COMPOSE;
    case AgentType.ASK:
    case AgentType.DEEP_RESEARCH:
    case AgentType.LEGACY_DATA_IMPORT:
    case AgentType.ANALYSIS:
    case AgentType.CO_SCIENTIST:
    case AgentType.ONBOARDING:
      return false;
    default:
      assertNever(agentType);
      return false;
  }
}

/**
 * Indicate if the chat type should show progress UI (tool calls, activity tracking).
 *
 * TODO: This should be unconditionally true.
 */
export function doesChatTypeShowProgress(chatType: ChatType): boolean {
  switch (chatType) {
    case ChatType.UNIFIED:
    case ChatType.DEEP_RESEARCH:
      return true;
    case ChatType.DATA_CAPTURE:
      return true;
    case ChatType.LEGACY_DATA_IMPORT:
      return true;
    case ChatType.ANALYSIS:
      return true;
    case ChatType.ONBOARDING:
      return false;
    default:
      assertNever(chatType);
      return false;
  }
}
